import { Component, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/services/LanguageService';
import { uploadFileService } from 'src/app/services/uploadFileService';

@Component({
  selector: 'app-data-upload-url',
  templateUrl: './data-upload-url.component.html',
  styleUrls: ['./data-upload-url.component.scss'],
})
export class DataUploadUrlComponent implements OnInit {
  form: FormGroup;
  isSubmitted = false;
  uploadedUrls: string[] = [];
  urlInputs: FormControl[] = [];

  invalidFiles: any[] = [];
  errorMessage: string = '';

  // List of file extensions to restrict (add more as needed)
  restrictedFileExtensions: string[] = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.csv',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.zip',
    '.rar',
    '.tar',
    '.gz',
    '.mp3',
    '.mp4',
    '.avi',
    '.mov',
    '.wmv',
    '.svg',
    '.webp',
    '.json',
    '.xml',
    '.rtf',
    '.odt',
    '.ods',
    '.odp',
    '.bmp',
    '.tiff',
    '.psd',
    '.ai',
    '.eps',
    '.mkv',
    '.flv',
    '.wav',
    '.ogg',
    '.7z',
    '.apk',
    '.exe',
    '.dll',
    '.iso',
    '.bin',
    '.bat',
    '.sh',
  ];

  constructor(
    public dialogRef: MatDialogRef<DataUploadUrlComponent>,
    private formBuilder: FormBuilder,
    private uploadFileService: uploadFileService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    this.translate.setDefaultLang(this.languageService.getLanguage());

    this.form = this.formBuilder.group({
      URL: [''],
    });
  }

  ngOnInit(): void {
    // Retrieve the previously uploaded URLs from the data service
    this.uploadedUrls = this.uploadFileService.getUploadedUrls();
    this.urlInputs = this.uploadedUrls.map(
      (url) => new FormControl(url, [Validators.required])
    );

    if (this.urlInputs.length === 0) {
      this.addURLInput();
    }

    //Retrive invalid files if exist
    this.invalidFiles = this.uploadFileService.getInvalidFiles();

    // Listen for changes in each URL input control
    this.urlInputs.forEach((inputControl, index) => {
      inputControl.valueChanges.subscribe(() => {
        const removedUrl = this.uploadedUrls[index];

        // Remove the existing file from invalidFiles if it exists
        if (removedUrl) {
          this.invalidFiles = this.invalidFiles.filter(
            (file) => file.data_key !== removedUrl
          );
          this.uploadFileService.saveInvalidFiles(this.invalidFiles);
        }
      });
    });
  }

  onUrlInput(event: any, control: FormControl) {
      const input = event.target;
      let value = control.value || '';

      // If the value is exactly "https://", don't do anything on input
      if (value === 'https://') {
        setTimeout(() => {
          input.setSelectionRange(8, 8);
        }, 0);
        return;
      }

      // If the value is less than 8 characters, reset to https://
      if (value.length < 8) {
        control.setValue('https://', { emitEvent: false });
        setTimeout(() => {
          input.setSelectionRange(8, 8);
        }, 0);
        return;
      }

      // If user tries to delete or modify the https:// prefix
      if (!value.startsWith('https://')) {
        const withoutPrefix = value.replace(/^https?:\/\//, '');
        control.setValue('https://' + withoutPrefix, { emitEvent: false });
        setTimeout(() => {
          const cursorPosition = input.selectionStart;
          const newPosition = Math.max(8, cursorPosition);
          input.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }

    onUrlKeydown(event: KeyboardEvent, control: FormControl) {
      const input = event.target as HTMLInputElement;
      const value = control.value || '';
      const cursorPosition = input.selectionStart || 0;

      // Prevent backspace or delete if cursor is at or before position 8
      if ((event.key === 'Backspace' && cursorPosition <= 8) || 
          (event.key === 'Delete' && cursorPosition < 8)) {
        event.preventDefault();
        return;
      }
    }


  get errorControl() {
    return this.form.controls;
  }

  addURLInput() {
    const newControl = new FormControl('https://', [Validators.required]);
    this.urlInputs.push(newControl);
  }

  removeURLInput(index: number) {
    const removedUrl = this.urlInputs[index].value; // Store the URL value
    this.urlInputs.splice(index, 1);

    // Remove the existing file from invalidFiles if it exists
    if (removedUrl) {
      this.invalidFiles = this.invalidFiles.filter(
        (file) => file.data_key !== removedUrl
      );
      this.uploadFileService.saveInvalidFiles(this.invalidFiles);
    }

    this.uploadedUrls = this.uploadFileService.getUploadedUrls();
    this.uploadedUrls = this.uploadedUrls.filter(
      (savedUrl) => savedUrl !== removedUrl
    ); // Filter using the stored URL value
    this.uploadFileService.setUploadedUrl(this.uploadedUrls);
  }

  onSubmit() {
    if (
      this.urlInputs.length === 0 ||
      this.urlInputs.every((input) => input.value === '')
    ) {
      this.isSubmitted = true;
      this.errorMessage = 'At least one URL is required.';
      return;
    }

    // Check if any of the URLs are not valid (not starting with "https://")
    const hasInvalidURL = this.urlInputs.some(
      (input) => !this.isURLValid(input.value)
    );
    if (hasInvalidURL) {
      this.isSubmitted = true;
      this.errorMessage = 'Part of the URL is not formatted correctly!';
      return;
    }

    if (this.form.valid) {
      this.uploadedUrls = this.urlInputs
        .map((input) => input.value)
        .filter((value) => value !== '');

      this.uploadFileService.setUploadedUrl(this.uploadedUrls);
      this.dialogRef.close(this.uploadedUrls);
    }
  }

  resetError() {
    this.isSubmitted = false;
  }

  closeForm() {
    this.uploadFileService.setUploadedUrl(this.uploadedUrls);
    this.dialogRef.close(this.uploadedUrls);
  }

  isInvalidURL(url: string): boolean {
    return this.invalidFiles.some((file) => file.data_key === url);
  }

  isURLValid(url: string): boolean {
    // Must start with https:// and have content after it
    if (!url || !url.startsWith('https://') || url === 'https://') {
      return false;
    }
    
    // Check for duplicate protocols (invalid URLs like https://https:// or https://http://)
    if (url.match(/^https?:\/\/https?:\/\//)) {
      return false;
    }
    
    // Extract the part after https://
    const urlContent = url.substring(8); // Remove 'https://'
    
    // Check if there's actual content and a valid domain structure
    if (!urlContent || !urlContent.includes('.') || !/\.[^.]{2,}$/.test(url)) {
      return false;
    }
    
    // Restrict URLs that end with a file extension
    const lowerUrl = url.toLowerCase().split('?')[0].split('#')[0].trim();
    for (const ext of this.restrictedFileExtensions) {
      if (lowerUrl.endsWith(ext)) {
        return false;
      }
    }
    return true;
  }
}
